/**
 * WorkspaceChatPanel — full-height inline chat for CaseWorkspacePage.
 *
 * Uses the same chat API endpoints and query cache keys as AiAssistantPanel
 * (`["chat", caseId]`) so history is shared between the workspace and wizard.
 * This component is always visible (not a floating overlay), so it has no
 * open/close state. AiAssistantPanel is left completely untouched.
 *
 * When `greeting` is provided and chat history is empty, a synthetic
 * assistant greeting is rendered as UI-only (not stored in DB, no AI call).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { chat, type ChatMessageItem } from "@/lib/endpoints";
import { DEFAULT_CHIPS, type ChipDef } from "@/lib/ai-chips";
import { cn } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";
import {
  ChatGatingFlow,
  type GatingState,
} from "@/components/workspace/ChatGatingFlow";
import {
  ChatUploadZone,
  MOCK_FIELDS,
  type PendingExtraction,
  type ConfirmedExtraction,
} from "@/components/workspace/ChatUploadZone";
import { ExtractionConfirmCard } from "@/components/workspace/ExtractionConfirmCard";
import { GuidancePromptCard, type NBA } from "@/components/workspace/GuidancePromptCard";
import type { GateRequest } from "@/lib/api-types";

/** Passed from CaseWorkspacePage — computed from existing case/eval/job data. */
export interface WorkspaceGreeting {
  /** Main greeting body shown in the synthetic assistant bubble. */
  text: string;
  /** Optional inline action beneath the greeting text. */
  cta?: {
    label: string;
    /** Internal React Router path. Used when onCtaClick is absent. */
    href?: string;
    /** When present, renders a <button> instead of <Link>. Takes priority. */
    onCtaClick?: () => void;
  };
}

interface Props {
  caseId: string;
  /** When provided and history is empty, renders a synthetic greeting instead
   *  of the generic empty-state chips. Pure UI — never stored in the DB. */
  greeting?: WorkspaceGreeting;
  // ── T3: in-chat gating flow ──────────────────────────────────────────────
  /** True while the eligibility screening flow is running. Hides text input. */
  gatingActive?: boolean;
  /** Full gating state from CaseWorkspacePage (step, answers, submitting, error). */
  gatingState?: GatingState;
  /** Called when the user taps Yes/No on a gating question. */
  onGatingAnswer?: (field: keyof GateRequest, value: boolean) => void;
  /** Called when the user taps Retry after a gate API error. */
  onGatingRetry?: () => void;
  // ── T4: document upload flow ──────────────────────────────────────────────
  /** Document type to prompt the user to upload right now. Null = no prompt. */
  uploadPromptDoc?: string | null;
  /** Non-null while waiting for the user to review extracted fields. */
  pendingExtraction?: PendingExtraction | null;
  /** True while the upload HTTP request is in flight. */
  uploadingDoc?: boolean;
  /** Non-null if the last upload attempt failed. */
  uploadError?: string | null;
  /** Extractions the user has confirmed (session-only, used for sidebar badge). */
  confirmedExtractions?: ConfirmedExtraction[];
  /** Called when the user selects / drops a file in the upload zone. */
  onDocFile?: (file: File) => void;
  /** Called when the user taps "Looks correct →" on the extraction card. */
  onExtractionConfirm?: () => void;
  /** Called when the user taps "Skip for now" on the extraction card. */
  onExtractionSkip?: () => void;
  /** Called when the user taps "Skip for now →" on the upload zone. */
  onDocSkip?: () => void;
  // ── T7: next-best-action guidance ────────────────────────────────────────
  /** Non-null when the NBA guidance card should be shown in the chat. */
  nba?: NBA | null;
}

type SendState = "idle" | "streaming";

export function WorkspaceChatPanel({
  caseId,
  greeting,
  gatingActive = false,
  gatingState,
  onGatingAnswer,
  onGatingRetry,
  uploadPromptDoc = null,
  pendingExtraction = null,
  uploadingDoc = false,
  uploadError = null,
  confirmedExtractions: _confirmedExtractions = [],
  onDocFile,
  onExtractionConfirm,
  onExtractionSkip,
  onDocSkip,
  nba = null,
}: Props) {
  // True when any upload flow step is active — hides text input & chips.
  const uploadActive = !!(uploadPromptDoc || pendingExtraction);
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [sendState, setSendState] = useState<SendState>("idle");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user is near the bottom of the thread. Stored
  // as a ref (not state) so onScroll doesn't re-render the whole
  // panel on every wheel tick.
  const userNearBottomRef = useRef(true);
  const queryClient = useQueryClient();

  // Shared query cache key with AiAssistantPanel — messages written by the
  // wizard's AI panel appear here instantly and vice-versa.
  const historyQ = useQuery({
    queryKey: ["chat", caseId],
    queryFn: () => chat.history(caseId),
    staleTime: 0,
    retry: false,
  });

  const aiAvailable = historyQ.data?.available ?? true;
  const messages: ChatMessageItem[] = historyQ.data?.items ?? [];
  const isStreaming = sendState === "streaming";
  const isLoading = historyQ.isLoading;

  // Auto-scroll the thread to the bottom on new content. Watches:
  //   - messages (history fetch / optimistic user message)
  //   - streamingText (SSE chunks)
  //   - gatingState.step (eligibility Q&A advances without touching
  //     messages — earlier oversight that left gating answers below
  //     the fold)
  //   - upload / extraction / NBA cards (each can push the input
  //     area off-screen on small viewports)
  //
  // Behaviour:
  //   - skip when the user has scrolled up to read history
  //   - instant scroll during streaming (smooth animations
  //     compound across rapid updates and never catch the bottom)
  //   - defer to next frame so the DOM has the new node measured
  useEffect(() => {
    if (!userNearBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: isStreaming ? "auto" : "smooth",
      });
    });
    return () => cancelAnimationFrame(id);
  }, [
    messages,
    streamingText,
    isStreaming,
    gatingState?.step,
    gatingState?.answers,
    uploadPromptDoc,
    pendingExtraction,
    nba,
  ]);

  function handleThreadScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    userNearBottomRef.current = distance < 120;
  }

  // Focus input on mount.
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  async function handleSend(overrideMsg?: string) {
    const msg = (overrideMsg ?? input).trim();
    if (!msg || isStreaming) return;

    setInput("");
    setError(null);
    setSendState("streaming");
    setStreamingText("");

    // Optimistic user message.
    queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
      ["chat", caseId],
      (prev) => ({
        items: [
          ...(prev?.items ?? []),
          { id: `opt-${Date.now()}`, role: "user" as const, content: msg, created_at: new Date().toISOString() },
        ],
        total: (prev?.total ?? 0) + 1,
        available: prev?.available ?? true,
      }),
    );

    try {
      const reader = await chat.sendStream(caseId, msg);
      const decoder = new TextDecoder();
      let accumulated = "";
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (!data || data.includes('"message_id"')) continue;
          try {
            const parsed = JSON.parse(data) as { text?: string; error?: string };
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) {
              accumulated += parsed.text;
              setStreamingText(accumulated);
            }
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== "Unexpected end of JSON input") {
              throw parseErr;
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSendState("idle");
      setStreamingText("");
      queryClient.invalidateQueries({ queryKey: ["chat", caseId] });
    }
  }

  function handleChipClick(chip: ChipDef) {
    if (aiAvailable) {
      handleSend(chip.question);
    } else {
      // Static fallback: inject question + static answer directly into cache.
      const ts = Date.now();
      queryClient.setQueryData<{ items: ChatMessageItem[]; total: number; available: boolean }>(
        ["chat", caseId],
        (prev) => ({
          items: [
            ...(prev?.items ?? []),
            { id: `static-user-${ts}`, role: "user" as const, content: chip.question, created_at: new Date().toISOString() },
            { id: `static-ai-${ts}`, role: "assistant" as const, content: chip.staticAnswer, created_at: new Date().toISOString() },
          ],
          total: (prev?.total ?? 0) + 2,
          available: false,
        }),
      );
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleClear() {
    if (!confirm("Clear conversation history?")) return;
    await chat.clear(caseId);
    queryClient.setQueryData(["chat", caseId], { items: [], total: 0, available: aiAvailable });
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden" style={{ minHeight: "calc(100dvh - 11rem)" }}>
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 leading-tight">N-400 Assistant</p>
            <p className="text-[10px] text-slate-400">
              {aiAvailable ? "AI-powered · not legal advice" : "Quick answers · not legal advice"}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={handleClear}
            className="text-xs text-slate-400 hover:text-red-500 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Disclaimer strip */}
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 shrink-0">
        <p className="text-[11px] text-amber-700 leading-snug">
          <strong>Not legal advice.</strong> For complex situations, consult a licensed immigration attorney.
        </p>
      </div>

      {/* Unavailable banner */}
      {!isLoading && !aiAvailable && (
        <div className="mx-4 mt-3 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 shrink-0">
          <p className="text-xs text-slate-500 leading-snug">
            Live AI responses are not available in this environment. Click a question below for a pre-written answer.
          </p>
        </div>
      )}

      {/* Message thread */}
      <div
        ref={scrollRef}
        onScroll={handleThreadScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
      >
        {isLoading && (
          <div className="flex justify-center py-10">
            <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state: personalized greeting (if provided) or generic chips.
            Chips are hidden while gating or upload flow is active so the user
            can only interact via the active flow's controls. */}
        {!isLoading && messages.length === 0 && !isStreaming && (
          greeting
            ? <GreetingBubble
                greeting={greeting}
                chips={(gatingActive || uploadActive) ? [] : DEFAULT_CHIPS}
                onChipClick={handleChipClick}
              />
            : !(gatingActive || uploadActive) && (
                <ChatEmptyState chips={DEFAULT_CHIPS} onChipClick={handleChipClick} />
              )
        )}

        {/* In-chat gating flow (T3) — renders below history/greeting when active */}
        {gatingActive && gatingState && onGatingAnswer && (
          <ChatGatingFlow
            step={gatingState.step}
            answers={gatingState.answers}
            submitting={gatingState.submitting}
            error={gatingState.error}
            onAnswer={onGatingAnswer}
            onRetry={onGatingRetry ?? (() => {})}
          />
        )}

        {/* Compact chips when messages exist */}
        {!isLoading && messages.length > 0 && !isStreaming && !uploadActive && (
          <div className="flex flex-wrap gap-1.5 pb-1">
            {DEFAULT_CHIPS.slice(0, 3).map((c) => (
              <button
                key={c.question}
                onClick={() => handleChipClick(c)}
                disabled={isStreaming}
                className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-full transition-colors disabled:opacity-50"
              >
                {c.question}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}

        {/* T4: document upload zone — shown after chat history, when a doc is queued */}
        {uploadPromptDoc && !pendingExtraction && onDocFile && (
          <ChatUploadZone
            documentType={uploadPromptDoc}
            uploading={uploadingDoc}
            error={uploadError}
            onFile={onDocFile}
            onSkip={onDocSkip ?? (() => {})}
          />
        )}

        {/* T4: extraction confirmation card — shown after a successful upload */}
        {pendingExtraction && onExtractionConfirm && onExtractionSkip && (
          <ExtractionConfirmCard
            documentType={pendingExtraction.documentType}
            fileName={pendingExtraction.fileName}
            fields={MOCK_FIELDS[pendingExtraction.documentType] ?? []}
            onConfirm={onExtractionConfirm}
            onSkip={onExtractionSkip}
          />
        )}

        {/* T7: NBA guidance card — shown after upload flow completes, hidden during active flows */}
        {nba && !gatingActive && !uploadActive && (
          <GuidancePromptCard nba={nba} />
        )}

        {/* Live streaming bubble */}
        {isStreaming && <StreamingBubble text={streamingText} />}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-600">
            {error}
            <button
              onClick={() => {
                setError(null);
                const lastUser = [...messages].reverse().find((m) => m.role === "user");
                if (lastUser) handleSend(lastUser.content);
              }}
              className="ml-2 underline font-medium"
            >
              Retry
            </button>
          </div>
        )}

      </div>

      {/* Input area — hidden while gating or upload flow is active */}
      {aiAvailable && !gatingActive && !uploadActive && (
        <div className="shrink-0 border-t border-slate-100 px-4 py-3">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              placeholder="Ask a question about your N-400…"
              rows={2}
              className={cn(
                "flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm placeholder:text-slate-400",
                "focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400",
                "disabled:opacity-50 disabled:bg-slate-50 transition-colors",
              )}
            />
            <button
              onClick={() => handleSend()}
              disabled={isStreaming || !input.trim()}
              className={cn(
                "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all",
                "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed",
              )}
              aria-label="Send message"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">Enter to send · Shift+Enter for new line</p>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * Renders a synthetic assistant greeting bubble when chat history is empty.
 * UI-only — never stored in the database, no AI call made.
 * Standard quick-question chips appear below so the user can still ask freely.
 */
function GreetingBubble({
  greeting,
  chips,
  onChipClick,
}: {
  greeting: WorkspaceGreeting;
  chips: ChipDef[];
  onChipClick: (c: ChipDef) => void;
}) {
  return (
    <div className="flex flex-col gap-4 py-4">
      {/* Synthetic assistant message */}
      <div className="flex gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
          </svg>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {greeting.text}
          </p>
          {greeting.cta && (
            greeting.cta.onCtaClick ? (
              /* In-chat action (e.g. start eligibility flow) — no navigation */
              <button
                onClick={greeting.cta.onCtaClick}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 px-3.5 py-2 rounded-lg transition-colors"
              >
                {greeting.cta.label}
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            ) : greeting.cta.href ? (
              /* Navigation action — uses React Router Link */
              <Link
                to={greeting.cta.href}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3.5 py-2 rounded-lg transition-colors"
              >
                {greeting.cta.label}
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            ) : null
          )}
        </div>
      </div>

      {/* Standard chips below the greeting */}
      <div className="pl-9">
        <p className="text-xs text-slate-400 mb-2">Or ask me anything:</p>
        <div className="flex flex-col gap-2">
          {chips.slice(0, 3).map((c) => (
            <button
              key={c.question}
              onClick={() => onChipClick(c)}
              className="text-sm text-left text-blue-700 bg-blue-50 hover:bg-blue-100 px-3.5 py-2.5 rounded-xl transition-colors border border-blue-100 leading-snug"
            >
              {c.question}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatEmptyState({ chips, onChipClick }: { chips: ChipDef[]; onChipClick: (c: ChipDef) => void }) {
  return (
    <div className="flex flex-col items-center text-center py-10 px-4">
      <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-800 mb-1">Your N-400 assistant</p>
      <p className="text-xs text-slate-400 mb-6 max-w-sm leading-relaxed">
        Ask me anything about your application. I can explain requirements, flag potential issues,
        and guide you to the right section of the form.
      </p>
      <div className="w-full max-w-sm flex flex-col gap-2 text-left">
        {chips.map((c) => (
          <button
            key={c.question}
            onClick={() => onChipClick(c)}
            className="text-sm text-left text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-3 rounded-xl transition-colors border border-blue-100 leading-snug"
          >
            {c.question}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessageItem }) {
  const isUser = msg.role === "user";
  return (
    <div className={cn("flex gap-2.5", isUser ? "flex-row-reverse" : "flex-row")}>
      {!isUser && (
        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
          </svg>
        </div>
      )}
      <div className={cn(
        "max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed break-words",
        isUser
          ? "bg-blue-600 text-white rounded-tr-sm whitespace-pre-wrap"
          : "bg-slate-50 border border-slate-200 text-slate-700 rounded-tl-sm",
      )}>
        {/* User messages are plain text (no markdown rendering — what
            they typed is what they meant). Assistant messages run
            through Markdown so **bold** / `code` / lists / blockquotes
            render properly. */}
        {isUser ? msg.content : <Markdown>{msg.content}</Markdown>}
      </div>
    </div>
  );
}

function StreamingBubble({ text }: { text: string }) {
  return (
    <div className="flex gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
        </svg>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
        {text ? (
          <div className="text-sm text-slate-700 leading-relaxed break-words">
            <Markdown>{text}</Markdown>
            <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-text-bottom" />
          </div>
        ) : (
          <div className="flex gap-1 items-center py-1">
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0ms]" />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:300ms]" />
          </div>
        )}
      </div>
    </div>
  );
}
